import { RentRowKind, RentSortField, RentValueMode, type RentGroupBy, type RentSort } from '@rentguru/commons-utils';
import { type RentDensity } from '@up2rent/ui-new';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { FIRST_PAGE_INDEX } from './rentMatrixWindowUtils';
import {
  RENT_DEFAULT_SORT_BY_ROW_VIEW,
  RENT_PAGE_DEFAULT_ROW_VIEW,
  RENT_SORT_OPTIONS,
  type RentSortOption,
} from './rentTrackingConstants';
import { EMPTY_RENT_FILTERS, type RentTrackingFilterState } from './rentTrackingUtils';

export interface RentCellSelection {
  anchor: HTMLElement;
  monthIndex: number;
  rowId: string;
}

export interface ScopedRentCellSelection extends RentCellSelection {
  scopeKey: string;
}

export interface RentPlaceholderView {
  fiscalYear: number;
  mode: RentValueMode;
  pageIndex?: number;
  rowView: RentRowKind;
}

export interface RentTrackingViewState {
  density?: RentDensity;
  filters: RentTrackingFilterState;
  fiscalYear: number;
  fitsComfortable: boolean;
  groupBy: RentGroupBy | null;
  mode: RentValueMode;
  modeBeforeForecast: RentValueMode | null;
  pageIndices: number[];
  rowView: RentRowKind;
  search: string;
  selection: ScopedRentCellSelection | null;
  sort: RentSort;
}

export enum RentTrackingViewActionType {
  CELL_SELECTED = 'cellSelected',
  COMFORTABLE_FIT_CHANGED = 'comfortableFitChanged',
  DENSITY_CHANGED = 'densityChanged',
  FILTERS_CHANGED = 'filtersChanged',
  FILTERS_CLEARED = 'filtersCleared',
  FISCAL_YEAR_CHANGED = 'fiscalYearChanged',
  GROUP_BY_CHANGED = 'groupByChanged',
  MODE_CHANGED = 'modeChanged',
  ROW_VIEW_CHANGED = 'rowViewChanged',
  SAVED_FILTER_APPLIED = 'savedFilterApplied',
  SEARCH_CHANGED = 'searchChanged',
  SELECTION_CLEARED = 'selectionCleared',
  SORT_CHANGED = 'sortChanged',
  VISIBLE_PAGES_CHANGED = 'visiblePagesChanged',
}

export type RentTrackingViewAction =
  | { type: RentTrackingViewActionType.CELL_SELECTED; selection: ScopedRentCellSelection }
  | { type: RentTrackingViewActionType.COMFORTABLE_FIT_CHANGED; fitsComfortable: boolean }
  | { type: RentTrackingViewActionType.DENSITY_CHANGED; density: RentDensity }
  | { type: RentTrackingViewActionType.FILTERS_CHANGED; filters: RentTrackingFilterState }
  | { type: RentTrackingViewActionType.FILTERS_CLEARED }
  | { type: RentTrackingViewActionType.FISCAL_YEAR_CHANGED; currentFiscalYear: number; fiscalYear: number }
  | { type: RentTrackingViewActionType.GROUP_BY_CHANGED; groupBy: RentGroupBy | null }
  | { type: RentTrackingViewActionType.MODE_CHANGED; mode: RentValueMode }
  | { type: RentTrackingViewActionType.ROW_VIEW_CHANGED; rowView: RentRowKind }
  | {
      type: RentTrackingViewActionType.SAVED_FILTER_APPLIED;
      filters: RentTrackingFilterState;
      groupBy: RentGroupBy | null;
    }
  | { type: RentTrackingViewActionType.SEARCH_CHANGED; search: string }
  | { type: RentTrackingViewActionType.SELECTION_CLEARED }
  | { type: RentTrackingViewActionType.SORT_CHANGED; sort: RentSort }
  | { type: RentTrackingViewActionType.VISIBLE_PAGES_CHANGED; pageIndices: number[] };

export type RentTrackingSavedView = Pick<
  RentTrackingViewState,
  'density' | 'filters' | 'fiscalYear' | 'groupBy' | 'mode' | 'modeBeforeForecast' | 'rowView' | 'search' | 'sort'
>;

export interface RentTrackingSavedViewLocationState {
  view: RentTrackingSavedView;
}

type RentModeState = Pick<RentTrackingViewState, 'mode' | 'modeBeforeForecast'>;

interface InitialRentTrackingView {
  filters: Partial<RentTrackingFilterState> | undefined;
  fiscalYear: number;
  savedView?: RentTrackingSavedView;
}

export const createRentTrackingViewState = ({
  filters,
  fiscalYear,
  savedView,
}: InitialRentTrackingView): RentTrackingViewState => ({
  fiscalYear,
  fitsComfortable: true,
  groupBy: null,
  mode: RentValueMode.COLLECTED,
  modeBeforeForecast: null,
  pageIndices: [FIRST_PAGE_INDEX],
  rowView: RENT_PAGE_DEFAULT_ROW_VIEW,
  search: '',
  selection: null,
  sort: RENT_DEFAULT_SORT_BY_ROW_VIEW[savedView?.rowView ?? RENT_PAGE_DEFAULT_ROW_VIEW],
  ...savedView,
  filters: { ...EMPTY_RENT_FILTERS, ...(savedView?.filters ?? filters) },
});

const withoutSelection = (state: RentTrackingViewState): RentTrackingViewState => ({ ...state, selection: null });

export const getSortForRowView = (sort: RentSort, previousRowView: RentRowKind, rowView: RentRowKind): RentSort =>
  isEqual(sort, RENT_DEFAULT_SORT_BY_ROW_VIEW[previousRowView]) ? RENT_DEFAULT_SORT_BY_ROW_VIEW[rowView] : sort;

export const getModeForFiscalYear = (
  { mode, modeBeforeForecast }: RentModeState,
  fiscalYear: number,
  currentFiscalYear: number
): RentModeState => {
  if (fiscalYear > currentFiscalYear) {
    return mode === RentValueMode.EXPECTED
      ? { mode, modeBeforeForecast }
      : { mode: RentValueMode.EXPECTED, modeBeforeForecast: mode };
  }
  return isNil(modeBeforeForecast)
    ? { mode, modeBeforeForecast }
    : { mode: modeBeforeForecast, modeBeforeForecast: null };
};

export const rentTrackingViewReducer = (
  state: RentTrackingViewState,
  action: RentTrackingViewAction
): RentTrackingViewState => {
  switch (action.type) {
    case RentTrackingViewActionType.CELL_SELECTED:
      return { ...state, selection: action.selection };
    case RentTrackingViewActionType.COMFORTABLE_FIT_CHANGED:
      return state.fitsComfortable === action.fitsComfortable
        ? state
        : { ...state, fitsComfortable: action.fitsComfortable };
    case RentTrackingViewActionType.DENSITY_CHANGED:
      return { ...withoutSelection(state), density: action.density };
    case RentTrackingViewActionType.FILTERS_CHANGED:
      return { ...withoutSelection(state), filters: action.filters };
    case RentTrackingViewActionType.FILTERS_CLEARED:
      return { ...withoutSelection(state), filters: EMPTY_RENT_FILTERS, groupBy: null };
    case RentTrackingViewActionType.FISCAL_YEAR_CHANGED:
      return {
        ...withoutSelection(state),
        ...getModeForFiscalYear(state, action.fiscalYear, action.currentFiscalYear),
        fiscalYear: action.fiscalYear,
      };
    case RentTrackingViewActionType.GROUP_BY_CHANGED:
      return { ...withoutSelection(state), groupBy: action.groupBy };
    case RentTrackingViewActionType.MODE_CHANGED:
      return { ...withoutSelection(state), mode: action.mode, modeBeforeForecast: null };
    case RentTrackingViewActionType.ROW_VIEW_CHANGED:
      return state.rowView === action.rowView
        ? state
        : {
            ...withoutSelection(state),
            pageIndices: [FIRST_PAGE_INDEX],
            rowView: action.rowView,
            sort: getSortForRowView(state.sort, state.rowView, action.rowView),
          };
    case RentTrackingViewActionType.SAVED_FILTER_APPLIED:
      return { ...withoutSelection(state), filters: action.filters, groupBy: action.groupBy };
    case RentTrackingViewActionType.SEARCH_CHANGED:
      return { ...withoutSelection(state), search: action.search };
    case RentTrackingViewActionType.SELECTION_CLEARED:
      return isNil(state.selection) ? state : withoutSelection(state);
    case RentTrackingViewActionType.SORT_CHANGED:
      return { ...withoutSelection(state), sort: action.sort };
    case RentTrackingViewActionType.VISIBLE_PAGES_CHANGED:
      return isEqual(state.pageIndices, action.pageIndices) ? state : { ...state, pageIndices: action.pageIndices };
    default:
      return state;
  }
};

export const getVisibleRentSelection = (
  selection: ScopedRentCellSelection | null,
  scopeKey: string
): RentCellSelection | null => (selection?.scopeKey === scopeKey ? selection : null);

export const getVisibleSelection = (state: RentTrackingViewState, scopeKey: string): RentCellSelection | null =>
  getVisibleRentSelection(state.selection, scopeKey);

export const getRentSortOptions = (rowView: RentRowKind): RentSortOption[] =>
  rowView === RentRowKind.LEASE
    ? RENT_SORT_OPTIONS.filter((option) => option.sort.field !== RentSortField.UNIT)
    : RENT_SORT_OPTIONS;

export const findRentSortOption = (sort: RentSort): RentSortOption | undefined =>
  RENT_SORT_OPTIONS.find((option) => option.sort.field === sort.field);

export const canReuseAsPlaceholder = (previous: RentPlaceholderView | undefined, next: RentPlaceholderView): boolean =>
  !isNil(previous) &&
  previous.pageIndex === next.pageIndex &&
  previous.fiscalYear === next.fiscalYear &&
  previous.mode === next.mode &&
  previous.rowView === next.rowView;
